import { useEffect, useMemo, useRef, useState } from "react";

import { Offer, SUPPORTED_OFFER_DURATIONS } from "@/api/content.ts";
import { useAppProperties, useOffers } from "@/hooks";
import { useTranslate } from "@/providers/context";
import { cn, sortByArray } from "@/utils/index.ts";

import { UserScreenWrapper } from "../User/components/UserScreenWrapper.tsx";
import { OfferCard } from "./components/OfferCard.tsx";
import { TermsLinks } from "./components/TermsLinks.tsx";

export const ForSaleScreen = () => {
  const { forSaleOffers } = useOffers();

  const { data } = useAppProperties();
  const { translate } = useTranslate();

  const [isOverflowing, setIsOverflowing] = useState(false);

  const offerContainerRef = useRef<HTMLDivElement>(null);

  const isFreeAccessApp = data?.FreeAccessApp === "1";
  const offerOrderProp = data?.TempOfferOrder?.split(",");

  const sortedByDuration = useMemo(() => {
    if (!forSaleOffers) {
      return [];
    }
    return [...forSaleOffers].sort((a: Offer, b: Offer) => {
      // First sort by price (free offers first)
      if (a.WebPriceUSD === 0 && b.WebPriceUSD !== 0) {
        return -1;
      }
      if (a.WebPriceUSD !== 0 && b.WebPriceUSD === 0) {
        return 1;
      }

      // Then sort by duration within each group
      return sortByArray(
        a.SubscriptionDuration,
        b.SubscriptionDuration,
        SUPPORTED_OFFER_DURATIONS,
      );
    });
  }, [forSaleOffers]);

  const offersByDisplayOrder = useMemo(() => {
    if (!forSaleOffers) {
      return [];
    }
    return [...forSaleOffers].sort((a: Offer, b: Offer) => {
      if (offerOrderProp) {
        return sortByArray(a.ProductId, b.ProductId, offerOrderProp);
      }

      // First sort by price (free offers first)
      if (a.WebPriceUSD === 0 && b.WebPriceUSD !== 0) {
        return -1;
      }
      if (a.WebPriceUSD !== 0 && b.WebPriceUSD === 0) {
        return 1;
      }

      // Then sort by duration within each group
      return sortByArray(
        a.SubscriptionDuration,
        b.SubscriptionDuration,
        SUPPORTED_OFFER_DURATIONS,
      );
    });
  }, [forSaleOffers, offerOrderProp]);

  // Get the first paid offer to use as a comparison for the percentage savings calculations
  const offerForPercentageComparison = useMemo(() => {
    const paidOffers = sortedByDuration.filter(
      ({ WebPriceUSD }) => WebPriceUSD !== 0,
    );

    if (paidOffers.length === 0) {
      return undefined;
    }

    return paidOffers[0];
  }, [sortedByDuration]);

  useEffect(() => {
    const container = offerContainerRef.current;
    if (!container) return;

    const checkOverflow = () => {
      const windowHeight = window.innerHeight;
      const containerHeight = container.offsetHeight;
      setIsOverflowing(
        containerHeight >
          windowHeight *
            (forSaleOffers && forSaleOffers.length < 2 ? 0.4 : 0.6),
      );
    };

    // Initial check
    checkOverflow();

    // Create resize observer
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(container);

    // Add window resize listener
    window.addEventListener("resize", checkOverflow);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [forSaleOffers]);

  if (!data || !forSaleOffers) {
    return null;
  }

  return (
    <UserScreenWrapper showCloseButton contentClassName="max-w-[392px]">
      <div className={isOverflowing ? "pb-[110px] sm:pt-12" : ""}>
        <h1 className="h1">
          {data?.CustomPurchaseScreen1_SubscriptionOnly_Text1}
        </h1>
        <h5 className="h5-regular pt-3">
          {data?.CustomPurchaseScreen1_SubscriptionOnly_Text2}
        </h5>

        <div className="flex flex-col gap-5 py-5" ref={offerContainerRef}>
          {offersByDisplayOrder.map((o: Offer) => (
            <OfferCard
              key={o.Id}
              offer={o}
              lowestDuration={sortedByDuration[0].SubscriptionDuration}
              comparativePrice={offerForPercentageComparison?.WebPriceUSD}
              comparativeDuration={
                offerForPercentageComparison?.SubscriptionDuration
              }
            />
          ))}
        </div>
      </div>
      <div
        className={cn(
          "p2-semibold subtitle text-item-secondary w-full text-center font-bold",
          isOverflowing
            ? "bg-surface-primary border-border absolute bottom-0 left-0 z-10 w-full border-t pb-5 pt-3"
            : "pt-4",
        )}
      >
        <div className="mx-auto w-fit font-bold">
          {!isFreeAccessApp && (
            <div className="subtitle text-item-secondary mb-[14px] max-w-[392px]">
              {translate(
                "Your subscription will automatically renew on a recurring basis until you cancel. You can manage your subscription on the Profile page.",
              )}
            </div>
          )}
          <div className="mb-2">
            <TermsLinks />
          </div>
          <div
            className="cursor-pointer"
            onClick={() => window.open(data.AppSupportURL ?? "")}
          >
            {translate("Need help?")} {translate("Contact us.")}
          </div>
        </div>
      </div>
    </UserScreenWrapper>
  );
};
